import { type ReactElement } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { EnforcementSeverityThresholdControl } from '../EnforcementSeverityThresholdControl';
import {
  SecretConfidenceThresholdControl,
  SecretConfidenceInfoTooltip,
} from '../SecretConfidenceThresholdControl';
import { Section } from '../Section';
import { usePersonalEnforcementSeverity } from './usePersonalEnforcementSeverity';
import { usePersonalSecretConfidence } from './usePersonalSecretConfidence';

type Props = {
  isEnforcePolicyModeEnabled: boolean;
  isSecretConfidenceThresholdEnabled: boolean;
};

export function PolicySettingsSection({
  isEnforcePolicyModeEnabled,
  isSecretConfidenceThresholdEnabled,
}: Props): ReactElement {
  const { severity, hasError, isSaving, hasAnyEnforcePolicy, onChange } =
    usePersonalEnforcementSeverity();
  const secretConfidence = usePersonalSecretConfidence();

  return (
    <Section
      title="Policy Settings"
      description="Configure global settings that govern how policies detect and enforce"
    >
      {isEnforcePolicyModeEnabled && (
        <Card>
          <CardHeader>
            <CardTitle>Enforcement Severity Threshold</CardTitle>
            <CardDescription>
              When policies are set to Enforce, this threshold determines the
              minimum severity at which protective actions are applied
            </CardDescription>
          </CardHeader>
          <CardContent>
            {hasError ? (
              <div className="rounded-md bg-destructive/15 p-4">
                <p className="text-sm text-destructive font-medium">
                  Failed to load enforcement severity threshold
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Please retry or contact support if the issue persists.
                </p>
              </div>
            ) : (
              <EnforcementSeverityThresholdControl
                value={severity}
                onChange={onChange}
                disabled={!hasAnyEnforcePolicy}
                saving={isSaving}
                canEdit={
                  true /* personal context: users always edit their own settings */
                }
              />
            )}
          </CardContent>
        </Card>
      )}

      {isSecretConfidenceThresholdEnabled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-1.5">
              Secret Alert Confidence Threshold
              <SecretConfidenceInfoTooltip />
            </CardTitle>
            <CardDescription>
              Secret leakage alerts below the chosen confidence are hidden from
              your dashboard, won't notify you, and won't be blocked in Enforce
              mode. Secrets matched by the regex engine have no confidence score
              and are always shown.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {secretConfidence.hasError ? (
              <div className="rounded-md bg-destructive/15 p-4">
                <p className="text-sm text-destructive font-medium">
                  Failed to load secret alert confidence threshold
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Please retry or contact support if the issue persists.
                </p>
              </div>
            ) : (
              <SecretConfidenceThresholdControl
                value={secretConfidence.confidence}
                onChange={secretConfidence.onChange}
                saving={secretConfidence.isSaving}
                canEdit={
                  true /* personal context: users always edit their own settings */
                }
              />
            )}
          </CardContent>
        </Card>
      )}
    </Section>
  );
}
